import type { CrosswordDoc } from './doc';
import type { Diagnostic } from '../../types';

/**
 * Diagnósticos do cliente — inline, antes do preview/save. O servidor continua sendo
 * o árbitro (`validate-crossword-draft.ts`), mas estes avisos pegam o erro de autoria
 * na hora. Cobrem os buracos que o `generateMatrix` corrompe em silêncio (§7.2) e as
 * mínimas do DTO. `error` bloqueia o save; `warning` só alerta.
 *
 * O reducer já impede a maioria das colocações inválidas durante a autoria; o valor
 * daqui é (a) pegar rascunho incompleto e (b) validar um jogo CARREGADO do servidor,
 * que pode chegar corrompido do legado.
 */
export function validate(doc: CrosswordDoc): Diagnostic[] {
  const out: Diagnostic[] = [];

  if (doc.words.length === 0) {
    out.push({ level: 'error', message: 'A cruzada precisa de ao menos uma palavra.' });
  }
  if (doc.deadCells.length === 0) {
    out.push({ level: 'error', message: 'A cruzada precisa de ao menos uma casa de dica.' });
  }

  for (const w of doc.words) {
    if (w.texto.length === 0) {
      out.push({ level: 'error', message: `A palavra "${w.ref}" está sem letras.`, ref: w.ref });
    }
    if (w.chave.trim() === '') {
      out.push({ level: 'error', message: `A palavra "${w.ref}" está sem dica.`, ref: w.ref });
    }
  }

  const deadKeys = new Set(doc.deadCells.map((c) => `${c.x},${c.y}`));

  // Percorre as letras de cada palavra procurando: (a) letra por cima de casa de dica
  // e (b) cruzamento com letras discordantes (último caso = gabarito errado).
  const letterAt = new Map<string, string>();
  for (const w of doc.words) {
    for (let i = 0; i < w.texto.length; i++) {
      const x = w.direcao ? w.x : w.x + i;
      const y = w.direcao ? w.y + i : w.y;
      const key = `${x},${y}`;
      const letter = w.texto[i];

      if (deadKeys.has(key)) {
        out.push({
          level: 'error',
          message: `A palavra "${w.ref}" passa por cima da casa de dica em (${x}, ${y}).`,
          ref: w.ref,
          x,
          y,
        });
      }

      const prev = letterAt.get(key);
      if (prev != null && prev !== letter) {
        out.push({
          level: 'error',
          message: `Cruzamento em conflito em (${x}, ${y}): "${prev}" ≠ "${letter}".`,
          x,
          y,
        });
      } else if (prev == null) {
        letterAt.set(key, letter);
      }
    }
  }

  for (const c of doc.deadCells) {
    if (c.palavra1Ref == null && c.palavra2Ref == null) {
      out.push({
        level: 'warning',
        message: `A casa de dica em (${c.x}, ${c.y}) não tem palavra pendurada.`,
        x: c.x,
        y: c.y,
      });
    }
  }

  return out;
}
